// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CachedLocationMerge,
  CachedLocationMergeItem,
} from '@/audit/api/types';
import { AuditMergedEntityLabel } from '@/audit/components/audit-merged-entity-label';
import { LocationType } from '@/location/api/types';
import { LOCATION_TYPE_LABELS } from '@/location/constants/location-type';
import {
  LocationField,
  appliesToLocationType,
} from '@/location/constants/merge-fields';

interface Props {
  property: CachedLocationMerge;
}

const keyLabels: Record<keyof CachedLocationMergeItem, string> = {
  address: 'Address',
  assignedCustomer: 'Company',
  consignee: 'Consignee',
  description: 'Description',
  id: 'Identifier',
  isInventory: 'Is inventory',
  isTransient: 'Is transient',
  key: 'Key',
  name: 'Name',
  type: 'Type',
};

type MergeItemKey = keyof CachedLocationMergeItem;

/** The audit's key names against the shared site/zone taxonomy that owns their scope */
const SCOPED_KEYS: Partial<Record<MergeItemKey, LocationField>> = {
  address: 'address',
  assignedCustomer: 'company',
  isTransient: 'isTransient',
  consignee: 'consignee',
  description: 'description',
};

// The audit records every field whichever kinds were merged, so the half that cannot apply is
// dropped here rather than rendered as a row of blanks.
function irrelevantKeys(
  result: CachedLocationMerge['newValue']
): MergeItemKey[] {
  const types = [result!.retained.type, result!.removed.type];
  // A merge across the two kinds keeps both sets, so the loss stays visible
  if (types[0] !== types[1]) return [];

  const locationType = types[0];

  return Object.entries(SCOPED_KEYS)
    .filter(([, field]) => !appliesToLocationType(field, locationType))
    .map(([key]) => key as MergeItemKey);
}

export function AuditMergedLocationEntityLabel(props: Props) {
  const result = props.property.newValue!; // Only new value is set for this prop

  return (
    <AuditMergedEntityLabel
      title="Merged location:"
      result={result}
      keyLabels={keyLabels}
      pinnedKeys={['type', 'name']}
      ignoredKeys={['id', 'key', ...irrelevantKeys(result)]}
      emptyValueLabel="Not set"
      getValueLabel={(key, value) =>
        key === 'type' && typeof value === 'string'
          ? LOCATION_TYPE_LABELS[value as LocationType]
          : undefined
      }
    />
  );
}
